/*
  Product dialog — shared open / close / Quick Shop hand-off.
  -----------------------------------------------------------
  One delegated controller for every product dialog in the theme. Consumers own their
  markup and their CSS and opt in with attributes alone:

    [data-product-dialog]                  the <dialog>
    [data-product-dialog-source="<name>"]  optional, names the caller in the event
    [data-product-dialog-open="<id>"]      a trigger, anywhere on the page
    [data-product-dialog-close]            a close control inside it
    [data-product-dialog-quickshop="<h>"]  a CTA offering that handle to Quick Shop
    [data-product-dialog-anchor]           optional, a box around the trigger to pin to

  With an anchor, each edge of that box's padded area is published as its distance from
  the matching viewport edge — --product-dialog-inline-start / -inline-end / -block-start
  / -block-end, each clamped to keep the dialog on screen — and both ends are marked:
  [data-product-dialog-pinned] on the dialog, [data-product-dialog-dimmed] on the anchor.
  Pick the pair for the corner the trigger sits in, at whichever breakpoints suit.

  A trigger may be an <a> pointing at the product: the dialog opens and the navigation
  is suppressed, so the same element is still a working link with JS off or on a browser
  without <dialog>. Buttons need no href and behave as before.

  Each CTA is a real link too. It dispatches a cancelable `quick-shop:open`, and only if
  a listener cancels does the navigation stop and the dialog close — with nothing
  listening, the customer lands on the product page.

  Replaces the per-block copies that _lifestyle-product-card and _mini-product-block
  each carried inline; both now render this file and use the attributes above.
*/
(function () {
  if (window.__productDialog) return;
  window.__productDialog = true;

  var active = null;
  var pending = 0;

  // A top-layer dialog is positioned against the viewport wherever it sits in the DOM,
  // so pinning it to a box on the page means measuring that box.
  function place(dialog) {
    var anchor = dialog.__pdAnchor;
    if (!anchor || !document.contains(anchor)) {
      dialog.style.removeProperty('--product-dialog-inline-start');
      dialog.style.removeProperty('--product-dialog-inline-end');
      dialog.style.removeProperty('--product-dialog-block-start');
      dialog.style.removeProperty('--product-dialog-block-end');
      dialog.removeAttribute('data-product-dialog-pinned');
      if (anchor) anchor.removeAttribute('data-product-dialog-dimmed');
      return;
    }
    dialog.setAttribute('data-product-dialog-pinned', '');
    anchor.setAttribute('data-product-dialog-dimmed', '');

    var rect = anchor.getBoundingClientRect();
    var pad = getComputedStyle(anchor);
    var rtl = getComputedStyle(document.documentElement).direction === 'rtl';
    // Not innerWidth/Height: a classic scrollbar sits inside those but outside the
    // box a fixed element is positioned against.
    var vw = document.documentElement.clientWidth;
    var vh = document.documentElement.clientHeight;

    // Clamped per axis, so any pair of edges still leaves the dialog on screen.
    function inline(px) {
      return Math.max(16, Math.min(px, vw - dialog.offsetWidth - 16)) + 'px';
    }
    function block(px) {
      return Math.max(16, Math.min(px, vh - dialog.offsetHeight - 16)) + 'px';
    }

    var s = dialog.style;
    s.setProperty('--product-dialog-inline-start',
      inline((rtl ? vw - rect.right : rect.left) + parseFloat(pad.paddingInlineStart)));
    s.setProperty('--product-dialog-inline-end',
      inline((rtl ? rect.left : vw - rect.right) + parseFloat(pad.paddingInlineEnd)));
    s.setProperty('--product-dialog-block-start',
      block(rect.top + parseFloat(pad.paddingBlockStart)));
    s.setProperty('--product-dialog-block-end',
      block(vh - rect.bottom + parseFloat(pad.paddingBlockEnd)));
  }

  function schedule() {
    if (!active || pending) return;
    pending = requestAnimationFrame(function () {
      pending = 0;
      if (active) place(active);
    });
  }

  window.addEventListener('scroll', schedule, { passive: true });
  window.addEventListener('resize', schedule);

  function openDialog(id, trigger, event) {
    var dialog = document.getElementById(id);
    if (!dialog || typeof dialog.showModal !== 'function') return;

    // Only once the dialog is genuinely opening: an unsupported browser falls through
    // to the anchor's own navigation rather than losing the click entirely.
    if (event && trigger.tagName === 'A') event.preventDefault();

    // Every close path (Esc, backdrop, button) fires `close`, so park the return
    // target on the dialog rather than tracking each exit separately.
    dialog.__pdReturnFocus = trigger;
    dialog.__pdAnchor = trigger.closest('[data-product-dialog-anchor]');
    dialog.showModal();
    active = dialog;
    place(dialog);

    var first = dialog.querySelector(
      '[data-product-dialog-quickshop], [data-product-dialog-close]'
    );
    if (first) first.focus();
  }

  function quickShop(event, handle, source) {
    var owner = source.closest('[data-product-dialog]');
    var detail = {
      handle: handle,
      url: source.getAttribute('href'),
      source: (owner && owner.getAttribute('data-product-dialog-source')) || 'product-dialog',
    };
    var ev = new CustomEvent('quick-shop:open', {
      detail: detail,
      cancelable: true,
      bubbles: true,
    });
    if (!source.dispatchEvent(ev)) {
      event.preventDefault();
      if (owner && owner.open) owner.close();
    }
  }

  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;

    var opener = e.target.closest('[data-product-dialog-open]');
    if (opener) {
      openDialog(opener.getAttribute('data-product-dialog-open'), opener, e);
      return;
    }

    var cta = e.target.closest('[data-product-dialog-quickshop]');
    if (cta) {
      quickShop(e, cta.getAttribute('data-product-dialog-quickshop'), cta);
      return;
    }

    var closer = e.target.closest('[data-product-dialog-close]');
    if (closer) {
      var owner = closer.closest('[data-product-dialog]');
      if (owner) owner.close();
      return;
    }

    // A backdrop click targets the dialog element itself.
    if (e.target.matches && e.target.matches('[data-product-dialog]')) {
      e.target.close();
    }
  });

  // `close` does not bubble — listen in the capture phase.
  document.addEventListener(
    'close',
    function (e) {
      var dialog = e.target;
      if (!dialog || !dialog.matches || !dialog.matches('[data-product-dialog]')) return;
      if (active === dialog) active = null;
      dialog.removeAttribute('data-product-dialog-pinned');
      if (dialog.__pdAnchor) dialog.__pdAnchor.removeAttribute('data-product-dialog-dimmed');
      dialog.__pdAnchor = null;
      var target = dialog.__pdReturnFocus;
      dialog.__pdReturnFocus = null;
      if (target && document.contains(target)) target.focus();
    },
    true
  );

  /* ---------- Theme editor lifecycle ---------- */
  /* Drop the return target first: the block is still attached here, so the close
     handler would otherwise focus a control that is about to be removed. */
  document.addEventListener('shopify:section:unload', function (e) {
    var dialogs = e.target.querySelectorAll('[data-product-dialog]');
    for (var i = 0; i < dialogs.length; i++) {
      dialogs[i].__pdReturnFocus = null;
      if (active === dialogs[i]) active = null;
      if (dialogs[i].open) dialogs[i].close();
    }
  });
})();
